import { ZoomInOutlined } from '@mui/icons-material'
import { Box, styled, useTheme } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import DrawingPreviewDialog from './DrawingPreviewDialog'

const ZOOM_BUTTON_CLASS = 'drawing-zoom'

interface DrawingThumbProps {
  url?: string
  alt: string
  selected?: boolean
  onSelect?: () => void
}

const DrawingThumb: React.FC<DrawingThumbProps> = ({ url, alt, selected, onSelect }) => {
  const theme = useTheme()
  const { t } = useTranslation('position')
  const { showDialog } = useDialogCtx()
  const [hasFailed, setHasFailed] = useState(false)

  if (!url || hasFailed) return <ThumbPlaceholder />

  const openPreview = (event: React.MouseEvent) => {
    event.stopPropagation()
    showDialog(DrawingPreviewDialog, {
      opts: { maxWidth: 'lg', fullWidth: true },
      componentProps: { url, drawingNumber: alt, selected, onSelect },
    })
  }

  return (
    <ThumbFrame>
      <ThumbImage src={url} alt={alt} onError={() => setHasFailed(true)} />
      <ZoomButtonSlot className={ZOOM_BUTTON_CLASS}>
        <GridActionButton
          toolTipTitle={String(t('configure-preview-drawing'))}
          aria-label={String(t('configure-preview-drawing'))}
          buttonIcon={
            <ZoomInOutlined sx={{ fontSize: 18 }} htmlColor={getThemeColor(theme, MUIThemeColors.primaryMain)} />
          }
          onClick={openPreview}
          onKeyDown={event => event.stopPropagation()}
          width="26px"
          height="26px"
          border={0}
          shadowWeight={1}
          sx={{ borderRadius: '50%' }}
        />
      </ZoomButtonSlot>
    </ThumbFrame>
  )
}

export default DrawingThumb

const ZoomButtonSlot = styled('span')({
  position: 'absolute',
  right: 4,
  bottom: 4,
  opacity: 0,
  transition: 'opacity .15s',
  '&:focus-within': { opacity: 1 },
  '@media (hover: none)': { opacity: 1 },
})

const ThumbPlaceholder = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
}))

const ThumbFrame = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  [`&:hover .${ZOOM_BUTTON_CLASS}`]: { opacity: 1 },
})

const ThumbImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))
